import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import ProviderStatementEmailTemplate from 'models/core/providerStatementEmailTemplate/Model.ProviderStatementEmailTemplate';
import providerStatementEmailTemplateDeleteApi from 'models/core/providerStatementEmailTemplate/delete/Api.ProviderStatementEmailTemplate.Delete';
import providerStatementEmailTemplateSaveApi from 'models/core/providerStatementEmailTemplate/save/Api.ProviderStatementEmailTemplate.Save';
import StatementEmailTemplate from 'models/core/statementEmailTemplate/Model.StatementEmailTemplate';
import Provider from 'models/productManagement/provider/Model.Provider';
import { useMemo } from 'react';
import { providerStatementEmailTemplateIndexRoute } from './ProviderStatementEmailTemplate.Index';

export enum StatementEmailTriggers {
  SendEmail = 'ANYSTATUS_SEND_EMAIL',
  DistributorSendEmail = 'DISTRIBUTOR_SEND_EMAIL',
}

export enum ProviderStatementEmailTemplateAction {
  EDIT = 'edit',
  DELETE = 'delete',
}
export type VerifyProviderStatementEmailTemplateAction = Utils.VerifyExtends<
  Module.Sales.ProviderStatementEmailTemplate.Actions,
  Utils.ValueOf<typeof ProviderStatementEmailTemplateAction>
>;
export default function useProviderStatementEmailTemplate() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<
    Module.Sales.ProviderStatementEmailTemplate.Params,
    Module.Sales.ProviderStatementEmailTemplate.Query
  >({
    route: providerStatementEmailTemplateIndexRoute,
  });

  const canView =
    permissions.CREATE_EMAILTEMPLATE || permissions.EDIT_EMAILTEMPLATE || permissions.DELETE_EMAILTEMPLATE;
  const templateApi = useApi({ action: ProviderStatementEmailTemplate.list });
  const listApi = useApi({
    action: ProviderStatementEmailTemplate.list,
    default: { limit: defaultLimit, offset: defaultOffset },
    body: pageRouter.query.list,
    callback: (req) => {
      templateApi.execute((b) => b);
      pageRouter.redirect({}, { ...pageRouter.query, list: req });
    },
    wait: !canView,
  });

  const deleteApi = useApi({
    action: providerStatementEmailTemplateDeleteApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const saveApi = useApi({
    action: providerStatementEmailTemplateSaveApi,
    wait: true,
  });

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const availableStatuses = useMemo(() => {
    const statementStatuses = Object.values({ ...StatementEmailTriggers })?.map((status) => status);
    const usedStatuses = templateApi.payload?.data?.map((it) => it.statementStatusTrigger) || [];

    return [
      ...statementStatuses.filter((status: any) => !usedStatuses.includes(status)),
      ...[selected?.statementStatusTrigger],
    ].filter(Boolean);
  }, [templateApi.payload?.data, selected]);

  const providerListApi = useApi({ action: Provider.list });
  const { payload: statementEmailTemplates } = useApi(
    { action: StatementEmailTemplate.list, wait: !availableStatuses, body: { statementStatus: availableStatuses } },
    [availableStatuses]
  );

  return {
    canView,
    pageRouter,
    listApi,
    saveApi,
    deleteApi,
    selected,
    permissions,
    availableStatuses,
    statementEmailTemplates,
    providerListApi,
    views: {
      [ProviderStatementEmailTemplateAction.DELETE]: !!(
        pageRouter.params.action === ProviderStatementEmailTemplateAction.DELETE &&
        selected &&
        permissions.DELETE_EMAILTEMPLATE
      ),
      [ProviderStatementEmailTemplateAction.EDIT]: !!(
        pageRouter.params.action === ProviderStatementEmailTemplateAction.EDIT &&
        ((permissions.CREATE_EMAILTEMPLATE && !selected) || (permissions.EDIT_EMAILTEMPLATE && selected))
      ),
    },
  };
}

export function useProviderStatementEmailTemplateProvider() {
  return useDataProvider<ReturnType<typeof useProviderStatementEmailTemplate>>();
}
